import { Toast } from '@components/Toast/Toast';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Modal } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useModalRegistration } from '@stores/ModalStore';
import { Media } from '@typings/Media';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'sonner';
import css from './MediaRenameModal.module.scss';

export type MediaRenameModalRef = {
  open: () => void;
  close: () => void;
};

export const MediaRenameModal = observer(
  forwardRef<
    MediaRenameModalRef,
    {
      media: Media;
      onOpen?: () => void;
      onClose?: () => void;
    }
  >(({ media, onOpen: onOpenProp, onClose: onCloseProp }, ref) => {
    const [open, setOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const inputRef = useRef<HTMLInputElement | null>(null);
    const libraryFolder = useLibraryFolder();
    const mediaIdRef = useRef<string | null>(null);

    useModalRegistration('media-rename', open);

    const onOpen = () => {
      setOpen(true);
      onOpenProp?.();
    };
    const onClose = () => {
      setOpen(false);
      onCloseProp?.();
    };

    useImperativeHandle(ref, () => ({
      open: onOpen,
      close: onClose,
    }));

    useEffect(() => {
      if (open) {
        // Only reset the name if we're opening for a different media or first time
        if (mediaIdRef.current !== media._id) {
          mediaIdRef.current = media._id;
          setNewName(media.metadata?.name ?? '');
        }
        // Defer focus/select until after render
        requestAnimationFrame(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        });
      } else {
        // Reset the ref when modal closes
        mediaIdRef.current = null;
      }
    }, [open, media._id]);

    return (
      <Modal
        open={open}
        onClose={onClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className={css.modal} onClick={(e) => e.stopPropagation()}>
          <IconButton className={css.closeButton} onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
          <h2>Rename media</h2>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const oldName = media.metadata?.name;
                if (oldName !== newName && newName !== '') {
                  await libraryFolder.renameMedia(media._id, newName);
                  await libraryFolder.refreshMediaById(media._id);
                  toast.custom((t) => <Toast id={t} header="Media renamed" variant="success" />, {
                    duration: 3000,
                  });
                }
                setNewName('');
                onClose();
              } catch (error) {
                console.error(error);
              }
            }}
          >
            <div className={css.formFieldAndLabel}>
              <label htmlFor="media-name">Media name</label>
              <input
                ref={inputRef}
                type="text"
                id="media-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className={css.input}
              />
            </div>
            <div className={css.actionButtons}>
              <ActionButton variant="secondary" onClick={onClose} type="button">
                Cancel
              </ActionButton>
              <ActionButton type="submit">Rename</ActionButton>
            </div>
          </form>
        </div>
      </Modal>
    );
  }),
);
